import {DeviceType} from '../incase-domain/common/DeviceType.enum';
import {ELEMENT} from '../incase-domain/elements/base/ElementTypes';
import {FLIP_CARD_SIDE, IElementFlipCard} from '../incase-domain/elements/flipCard';
import {IElement} from '../incase-domain/elements';
import {IElementImageGallery} from '../incase-domain/elements/imageGallery';
import {IElementPhotoAnswerQuestion} from '../incase-domain/elements/question/photoAnswerQuestion';
import {IPresentation} from '../incase-domain/presentation';
import {ISlide} from '../incase-domain/slide';
import {getEffectiveBackgroundImage} from '../hooks/useSlideBackground';
import {filterAndSortMobileElements} from './presentation';

function getDeviceSpecificElements(slide: ISlide, device: DeviceType): IElement[] {
    const elements = slide.elements ?? [];
    if (device === DeviceType.MOBILE) {
        return filterAndSortMobileElements(elements);
    }
    return elements;
}

function collectElementImageUrls(element: IElement): string[] {
    const urls: string[] = [];

    if ('imageSrc' in element && typeof element.imageSrc === 'string' && element.imageSrc) {
        urls.push(element.imageSrc);
    }

    switch (element.type) {
        case ELEMENT.IMAGE_GALLERY:
            (element as IElementImageGallery).images?.forEach((img) => {
                if (img.imageSrc) urls.push(img.imageSrc);
            });
            break;
        case ELEMENT.FLIP_CARD: {
            const flipCard = element as IElementFlipCard;
            [FLIP_CARD_SIDE.FRONT, FLIP_CARD_SIDE.BACK].forEach((side) => {
                const sideElement = flipCard.flipCardElement?.[side];
                if (sideElement?.type === ELEMENT.IMAGE && sideElement.imageSrc) {
                    urls.push(sideElement.imageSrc);
                }
            });
            break;
        }
        case ELEMENT.PHOTO_ANSWER_QUESTION:
            (element as IElementPhotoAnswerQuestion).answers?.forEach((answer) => {
                if (answer.imgSrc) urls.push(answer.imgSrc);
            });
            break;
        default:
            break;
    }

    return urls;
}

function collectSlideImageUrls(presentation: IPresentation | undefined, device: DeviceType, slide: ISlide): string[] {
    const backgroundUrl = getEffectiveBackgroundImage(presentation, device, slide);
    const urls = backgroundUrl ? [backgroundUrl] : [];

    getDeviceSpecificElements(slide, device).forEach((element) => {
        urls.push(...collectElementImageUrls(element));
    });

    return urls;
}

function getSlideWindow(slides: ISlide[], index: number, radius = 6): ISlide[] {
    if (!slides.length || index < 0 || index >= slides.length) {
        return [];
    }

    const start = Math.max(0, index - radius);
    const end = Math.min(slides.length - 1, index + radius);
    const window: ISlide[] = [];
    for (let i = start; i <= end; i++) {
        window.push(slides[i]);
    }

    return window;
}

export function collectWindowImageUrls(
    presentation: IPresentation | undefined,
    device: DeviceType,
    slides: ISlide[],
    currentIndex: number
): string[] {
    const seen = new Set<string>();
    const result: string[] = [];

    getSlideWindow(slides, currentIndex).forEach((slide) => {
        collectSlideImageUrls(presentation, device, slide).forEach((url) => {
            if (!seen.has(url)) {
                seen.add(url);
                result.push(url);
            }
        });
    });

    return result;
}
